<template>
  <div
    class="legend-flowline"
    v-bind:class="{ 'no-size': !radiuses.length & !widths.length }"
  >
    <div class="size-legend">
      <div v-if="radiuses.length" class="circle-legend">
        <b-form-checkbox
          v-bind:checked="geometryVisible.staycircle"
          class="geometry-check"
          v-on:change="$_onChangeGeometry('staycircle')"
        />
        <div
          class="circle-geometry"
          v-bind:style="{ '--max-radius': `${maxRadius}px` }"
        >
          <div
            v-for="item of radiuses"
            v-bind:key="item.radius"
            class="circle-geometry-item"
            v-bind:style="{ '--radius': `${item.radius}px` }"
          />
        </div>
        <div class="circle-value">
          <div
            v-for="(item, index) of roundedRadiuses"
            v-bind:key="item.value"
            class="circle-text"
            v-bind:style="{
              '--radius': `${item.radius}px`,
              '--prev-radius': `${
                index === 0 ? 0 : radiuses[index - 1].radius
              }px`,
            }"
          >
            <span>{{ item.value }}</span>
            <span v-if="index === roundedRadiuses.length - 1">
              {{ `(${circleUnitText})` }}
            </span>
          </div>
        </div>
      </div>
      <div v-if="widths.length" class="line-legend">
        <b-form-checkbox
          v-bind:checked="geometryVisible.flowline"
          class="geometry-check"
          v-on:change="$_onChangeGeometry('flowline')"
        />
        <div
          class="line-geometry-container"
          v-bind:style="{ height: `${maxRadius * 2}px` }"
        >
          <div
            v-for="(item, index) of widths"
            v-bind:key="item.value"
            class="line-legend-item"
          >
            <hr
              class="line-geometry"
              v-bind:style="{ height: `${item.width}px` }"
            />
            <div class="line-text">
              <span>{{ item.value }}</span>
              <span v-if="index === widths.length - 1">
                {{ `(${$t('appAnalyzeLegendFlowline.unit.time')})` }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <app-analyze-legend-user
      v-bind:users="usersModified"
      v-bind:visible="visible.user"
      v-on="$listeners"
    />
  </div>
</template>

<script>
import AppAnalyzeLegendUser from './AppAnalyzeLegendUser.vue'

// 単位
const UNIT = {
  SECOND: 'second',
  MINUTE: 'minute',
}

/**
 * 指定キーの表示状態取得
 * @param {Boolean|Object} visible 表示状態
 * @param {String} key キー
 * @returns {Boolean} 表示状態
 */
const getVisibleByKey = (visible, key) => {
  return typeof visible === 'boolean' ? visible : visible[key]
}

export default {
  name: 'AppAnalyzeLegendFlowline',
  components: {
    AppAnalyzeLegendUser,
  },
  props: {
    /**
     * ユーザー
     */
    users: {
      type: Array,
      default: () => [],
    },
    /**
     * 半径データ
     */
    radiuses: {
      type: Array,
      defualt: () => [],
    },
    /**
     * 線幅データ
     */
    widths: {
      type: Array,
      default: () => [],
    },
    /**
     * 表示状態
     */
    visible: {
      type: Object,
      default: () => ({ geometry: true, user: true }),
    },
  },
  computed: {
    /**
     * 加工したユーザー
     * @type {Array.<Object>}
     */
    usersModified: function () {
      return this.users.map((u) => ({
        ...u,
      }))
    },
    /**
     * 図形種類の表示状態
     * @type {Object.<Boolean>}
     */
    geometryVisible: function () {
      return {
        staycircle: getVisibleByKey(this.visible.geometry, 'staycircle'),
        flowline: getVisibleByKey(this.visible.geometry, 'flowline'),
      }
    },
    /**
     * 最大半径
     * @type {Number}
     */
    maxRadius: function () {
      if (!this.radiuses || !this.radiuses.length) return 0
      return Math.max(...this.radiuses.map((item) => item.radius))
    },
    /**
     * 円の値の単位
     * @type {String}
     */
    circleUnit: function () {
      if (!this.radiuses || !this.radiuses.length) return UNIT.SECOND
      let minDiff = Infinity
      for (let i = 0; i < this.radiuses.length - 1; ++i) {
        const diff = this.radiuses[i + 1].value - this.radiuses[i].value
        if (diff < minDiff) {
          minDiff = diff
        }
      }
      return minDiff >= 60 ? UNIT.MINUTE : UNIT.SECOND
    },
    /**
     * 円の単位表示文字列
     * @type {String}
     */
    circleUnitText: function () {
      return this.$t(`appAnalyzeLegendFlowline.unit.${this.circleUnit}`)
    },
    /**
     * 単位で丸めた半径
     * @type {Array.<Object>}
     */
    roundedRadiuses: function () {
      if (!this.radiuses || !this.radiuses.length) return []
      const denominator = this.circleUnit === UNIT.MINUTE ? 60 : 1
      return this.radiuses.map((item) => ({
        ...item,
        value: Math.floor(item.value / denominator),
      }))
    },
  },
  methods: {
    /**
     * ジオメトリ変更時処理
     * @param {Number} type 図形種類
     */
    $_onChangeGeometry: function (type) {
      const visible = { ...this.geometryVisible }
      visible[type] = !visible[type]
      /**
       * ジオメトリ表示状態変更イベント
       * @param {Object} visible 各図形種類の表示状態
       */
      this.$emit('geometry-visible-changed', visible)
    },
  },
}
</script>

<style lang="scss" scoped>
$-indicator-size: 14px;

.legend-flowline {
  color: $text-color;
  font-size: $font-size-middle;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  row-gap: 24px;

  &.no-size {
    row-gap: 0px;
  }
}

.size-legend {
  column-gap: 24px;
  display: grid;
  grid-template-columns: max-content max-content;
}

.geometry-check {
  align-self: center;

  ::v-deep .custom-control-label {
    cursor: pointer;
  }
}

.circle-legend {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  column-gap: 8px;

  .circle-geometry {
    --max-radius: 0px;

    min-width: calc(var(--max-radius) * 2);
    height: 100%;
    display: flex;
    flex-direction: column-reverse;
    justify-content: flex-start;
    align-items: center;
    position: relative;

    .circle-geometry-item {
      --radius: 1px;
      $-size: calc(var(--radius) * 2);

      position: absolute;
      width: $-size;
      height: $-size;
      border: 1px solid $text-color;
      border-radius: 50%;
    }
  }

  .circle-value {
    height: 100%;
    display: flex;
    flex-direction: column-reverse;
    justify-content: flex-start;
    align-items: flex-start;

    .circle-text {
      --radius: 0px;
      --prev-radius: 0px;

      width: 100%;
      height: calc((var(--radius) - var(--prev-radius)) * 2);
      line-height: 1;
      text-align: left;
    }
  }
}

.line-legend {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  column-gap: 8px;
}

.line-geometry-container {
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-start;
  flex-wrap: nowrap;

  .line-legend-item {
    flex: 1;
    display: grid;
    grid-template-columns: 40px 1fr;
    align-items: center;
    column-gap: 16px;

    .line-geometry {
      width: 40px;
      background-color: $text-color;
      margin: 0;
    }

    .line-text {
      line-height: 1;
      text-align: left;
    }
  }
}
</style>
